import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { TranslateModule } from '@ngx-translate/core';
import { filter, take } from 'rxjs/operators';

import type { MethodTypeAcquisitionField } from '../../../state/method-type.state';

import { MethodAssetFieldDialogComponent } from './method-asset-field-dialog.component';

export enum AssetFieldColumn {
  DISPLAY_NAME = 'displayName',
  FIELD_PATH = 'fieldPath',
  DELETE = 'delete',
}

/**
 * Dumb component that renders the staged asset fields of a method type and lets
 * the user add or delete them. It holds no state: the parent owns the
 * `assetFields` array and reacts to the `fieldAdded` / `fieldDeleted` outputs,
 * staging the change in its own state (persistence happens only on save).
 *
 * Asset fields have no stable id, so deletions are keyed by row index. Adding
 * is driven through a small self-contained dialog this component opens itself.
 */
@Component({
  selector: 'ts-method-asset-fields-manager',
  templateUrl: './method-asset-fields-manager.component.html',
  styleUrls: ['./method-asset-fields-manager.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatTableModule, MatDialogModule, TranslateModule],
})
export class MethodAssetFieldsManagerComponent {
  @Input() assetFields: MethodTypeAcquisitionField[] = [];

  @Output() fieldAdded = new EventEmitter<MethodTypeAcquisitionField>();
  @Output() fieldDeleted = new EventEmitter<number>();

  public readonly columns: string[] = [
    AssetFieldColumn.DISPLAY_NAME,
    AssetFieldColumn.FIELD_PATH,
    AssetFieldColumn.DELETE,
  ];

  constructor(private readonly dialog: MatDialog) {}

  public get hasFields(): boolean {
    return this.assetFields.length > 0;
  }

  public onNewAssetField(): void {
    this.dialog
      .open<MethodAssetFieldDialogComponent, undefined, MethodTypeAcquisitionField | undefined>(
        MethodAssetFieldDialogComponent
      )
      .afterClosed()
      .pipe(
        take(1),
        filter((field): field is MethodTypeAcquisitionField => !!field)
      )
      .subscribe((field) => this.fieldAdded.emit(field));
  }

  public onDeleteField(index: number): void {
    this.fieldDeleted.emit(index);
  }
}
